import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PublicLayout } from '@/components/public';
import { t } from '@/i18n';
import { 
  FileText, Search, Send, Shield, Clock, CheckCircle, 
  ArrowRight, FileCheck, Scale, AlertTriangle, Building2
} from 'lucide-react';

const SHOW_SELF_SERVICE = false; // self-service option hidden for now
const SERVICES = [
  {
    icon: FileText,
    title: t("Preliminary Notices"),
    badge: t("Most Popular"),
    description: t("Protect your lien rights from day one. We handle research, generation, and certified mail delivery of your preliminary notices."),
    features: [
      t("State-compliant documents for all 50 states"),
      t("Property & recipient research included"),
      t("USPS certified mail with tracking"),
      t("Proof of service documentation"),
      t("Deadline tracking & reminders"),
    ],
    price: 'Starting at $35',
  },
  {
    icon: Scale,
    title: t("Mechanic's Liens"),
    badge: t("Full Service"),
    description: t("When payment isn't coming, secure your claim. We prepare and file mechanic's liens to protect your right to payment."),
    features: [
      t("State-specific lien documents"),
      t("Legal description research"),
      t("County recording coordination"),
      t("Certified mail to all parties"),
      t("Deadline monitoring"),
    ],
    price: 'Starting at $250',
  },
  {
    icon: Building2,
    title: t("Bond Claims"),
    badge: 'Specialized',
    description: t("For public projects and bonded work, we prepare and submit bond claims to surety companies."),
    features: [
      t("Miller Act claims (federal)"),
      t("State bond claim notices"),
      t("Surety research & contact"),
      t("Documentation preparation"),
      t("Certified delivery"),
    ],
    price: 'Starting at $125',
  },
  {
    icon: AlertTriangle,
    title: t("Notice of Intent to Lien"),
    badge: 'Expedited',
    description: t("A powerful collection tool. Send official notice of your intent to file a lien before actually filing."),
    features: [
      t("Final warning before lien"),
      t("Often prompts immediate payment"),
      t("State-compliant language"),
      t("Certified mail delivery"),
      t("Tracking & proof of service"),
    ],
    price: 'Starting at $125',
  },
  {
    icon: FileCheck,
    title: t("Florida Notice of Commencement"),
    badge: 'Florida',
    description: t("The owner's filing that your permit and first inspection depend on. We research the property, prepare it, notarize it online with the owner and record it with the county clerk."),
    features: [
      t("We pull the owner of record, parcel number and legal description"),
      t("The owner signs online with our notary — nothing for you to chase"),
      t("Recorded with the county and emailed back to you"),
      t("Job-site posting copy, notarized or certified"),
      t("Rush: same-day research and the owner chased by phone"),
    ],
    price: '$135 · $115 each at 25+ a month',
    link: '/florida-notice-of-commencement',
  },
  {
    icon: Search,
    title: t("Research Services"),
    badge: 'Add-On',
    description: t("Let our experts handle the research. We find property owners, general contractors, bond information, and more."),
    features: [
      t("Property owner identification"),
      t("General contractor lookup"),
      t("Bond company research"),
      t("Legal description retrieval"),
      t("County records search"),
    ],
    price: 'Starting at $17',
  },
  {
    icon: FileCheck,
    title: t("Lien Waivers & Releases"),
    badge: 'Free',
    description: t("Generate conditional and unconditional lien waivers for progress and final payments at no additional cost."),
    features: [
      t("Conditional progress waivers"),
      t("Unconditional progress waivers"),
      t("Conditional final waivers"),
      t("Unconditional final waivers"),
      t("State-specific forms"),
    ],
    price: t("Free with account"),
  },
];

const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-[#1e3a5f] via-[#2d4a6f] to-[#1e3a5f]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-[#F97316] text-white mb-4">{t("Full Service Solutions")}</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
            {t("Comprehensive Lien Document Services")}
          </h1>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto mb-8">
            {t("From preliminary notices to mechanic's liens, we handle every step of protecting your lien rights — research included.")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')} 
              className="bg-[#F97316] hover:bg-[#ea6a10] text-white font-semibold px-8"
              data-testid="services-get-started"
            >
              {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={() => navigate('/pricing')}
              className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#1e3a5f]"
            >
              {t("View Pricing")}
            </Button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16 lg:py-20 bg-slate-50" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {SERVICES.map((service, index) => (
              <Card 
                key={index} 
                className={`border-2 ${index === 0 ? 'border-[#F97316] shadow-lg shadow-orange-500/10' : 'border-slate-200'} hover:shadow-xl transition-all bg-white`}
                data-testid={`service-card-${index}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-12 h-12 rounded-lg ${index === 0 ? 'bg-[#F97316]' : 'bg-[#1e3a5f]'} flex items-center justify-center`}>
                      <service.icon className="h-6 w-6 text-white" />
                    </div>
                    <Badge className={index === 0 ? 'bg-[#F97316]' : index === 5 ? 'bg-green-600' : 'bg-[#1e3a5f]'}>
                      {service.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl text-[#1e3a5f]">{service.title}</CardTitle>
                  <CardDescription className="text-slate-600">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-lg font-bold text-[#F97316]">{service.price}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-16 lg:py-20 bg-white" data-testid="how-we-work">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">{t("How Our Full Service Works")}</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t("Let us handle the heavy lifting. Our team of professionals ensures your documents are accurate, compliant, and delivered on time.")}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { icon: FileText, title: t("Submit Order"), desc: t("Provide basic project information through our easy online form.") },
              { icon: Search, title: t("We Research"), desc: t("Our team researches property details, recipients, and requirements.") },
              { icon: FileCheck, title: t("Review & Approve"), desc: t("Review the prepared document and approve for sending.") },
              { icon: Send, title: t("We Send & Track"), desc: t("We mail via certified mail and track delivery for you.") },
            ].map((step, index) => (
              <div key={index} className="text-center relative">
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] border-t-2 border-dashed border-slate-300"></div>
                )}
                <div className="w-16 h-16 rounded-full bg-[#1e3a5f] text-white flex items-center justify-center mx-auto mb-4 relative z-10">
                  <step.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-[#1e3a5f] mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Self Service Option — hidden for now */}
      {SHOW_SELF_SERVICE && <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#1e3a5f] mb-4">{t("Self-Service Option")}</Badge>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">
                {t("Already Have All The Info?")}
              </h2>
              <p className="text-slate-600 mb-6">
                {t("If you already know the property owner, general contractor, and other recipient details, use our self-service option and save money. You provide the info, we generate and send the notice.")}
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  t("Lower cost per notice ($13 vs $45)"),
                  t("Same state-compliant documents"),
                  t("Same certified mail delivery"),
                  t("Same tracking and proof of service"),
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3 text-slate-700">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/register')} 
                className="bg-[#F97316] hover:bg-[#ea6a10]"
              >
                {t("Try Self-Service")}
              </Button>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-6">{t("Full Service vs Self-Service")}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 text-slate-600">{t("Feature")}</th>
                    <th className="text-center py-3 text-[#F97316]">{t("Full Service")}</th>
                    <th className="text-center py-3 text-[#1e3a5f]">{t("Self-Service")}</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Price', full: '$45', self: '$13' },
                    { feature: 'Research Included', full: '✓', self: '—' },
                    { feature: 'Document Generation', full: '✓', self: '✓' },
                    { feature: 'Certified Mail', full: '✓', self: '✓' },
                    { feature: 'USPS Tracking', full: '✓', self: '✓' },
                    { feature: 'Proof of Service', full: '✓', self: '✓' },
                  ].map((row, index) => (
                    <tr key={index} className="border-b border-slate-100">
                      <td className="py-3 text-slate-700">{row.feature}</td>
                      <td className="py-3 text-center">{row.full}</td>
                      <td className="py-3 text-center">{row.self}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>}

      {/* Why Choose Us */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#1e3a5f] mb-4">{t("Why Choose All American Notice?")}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Shield, title: t("State Compliant"), desc: t("Documents meet all state-specific requirements") },
              { icon: Clock, title: t("Fast Turnaround"), desc: t("Most orders processed within 24-48 hours") },
              { icon: Send, title: t("Certified Mail"), desc: t("Real USPS tracking with proof of delivery") },
              { icon: CheckCircle, title: t("Accuracy Guaranteed"), desc: t("Expert review on every document") },
            ].map((item, index) => (
              <div key={index} className="text-center p-6 rounded-xl bg-slate-50">
                <item.icon className="h-10 w-10 text-[#F97316] mx-auto mb-4" />
                <h3 className="font-bold text-[#1e3a5f] mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20 bg-[#1e3a5f]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">
            {t("Ready to Protect Your Lien Rights?")}
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            {t("Get started today. $35–$45 per notice. No monthly fees, no setup costs.")}
          </p>
          <Button 
            size="lg" 
            onClick={() => navigate('/register')} 
            className="bg-[#F97316] hover:bg-[#ea6a10] text-white text-lg px-10 h-14 font-semibold"
          >
            {t("Get Started")} <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>
    </PublicLayout>
  );
};

export default ServicesPage;
